import {
  Alert,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import { useParams } from 'wouter';
import {
  LEAD_STATUSES,
  useAddLeadComment,
  useLeadDetails,
  useUpdateLeadStatus,
  type LeadStatus,
} from '@/models/leads';
import { ActivityFeed } from '@/components/ActivityFeed/ActivityFeed';
import { useHasAnyPermission } from '@/models/permissions';
import Link from '@/components/Link';
import { ConvertLeadModal } from '@/pages/contacts/ConvertLeadModal';
import { buildLeadFeed } from './feed';
import { ActionsCard } from './ActionsCard';
import { IdentityCard } from './IdentityCard';
import { RequestNotesCard } from './RequestNotesCard';
import { MarketingCard } from './MarketingCard';
import { AssignAssigneeControl } from './AssignAssigneeControl';
import { StatusBadge } from './StatusBadge';
import { listUrl } from '@/lib/listMemory';

const STATUSES: LeadStatus[] = LEAD_STATUSES.filter((s) => s !== 'converted');
const CONVERTIBLE: LeadStatus[] = STATUSES.filter((s) => s !== 'Lost');

export default function LeadDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const { data, isLoading, error } = useLeadDetails(id);
  const updateStatus = useUpdateLeadStatus();
  const addComment = useAddLeadComment();

  const [statusDraft, setStatusDraft] = useState<LeadStatus | null>(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const canConvert = useHasAnyPermission([
    'contacts:write',
    'contacts:*',
    '*:*',
  ]);
  const canAssign = useHasAnyPermission(['leads:assign', 'leads:*', '*:*']);

  if (isLoading) return null;
  if (!data) {
    const msg = String(error ?? '').toLowerCase();
    const notFound = !error || msg.includes('not found') || msg.includes('404');
    return (
      <Paper withBorder p="xl" maw={520} mx="auto" mt="xl">
        <Stack gap="xs" align="center">
          <Title order={4}>
            {notFound ? 'Lead not accessible' : 'Failed to load lead'}
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            {notFound
              ? `Lead #${id} either does not exist or is assigned to another sales representative. Ask an admin or the current assignee to grant you access.`
              : 'Something went wrong while loading this lead. Try again or contact support if the problem persists.'}
          </Text>
          <Link to={`~${listUrl('/leads')}`}>Back to leads</Link>
        </Stack>
      </Paper>
    );
  }
  const lead = data.lead;
  const fullName =
    [lead.firstName, lead.lastName].filter(Boolean).join(' ') || '—';
  const isConverted = lead.status === 'converted';
  const isConvertible = (CONVERTIBLE as string[]).includes(lead.status);

  return (
    <Paper p="md" h="100%">
      <Container size="xl" px={0}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="md">
              {isConverted && lead.contactId && (
                <Alert color="blue" variant="light">
                  <Group gap="xs">
                    <Text span>Converted to contact —</Text>
                    <Link to={`~/contacts/${lead.contactId}`}>
                      open contact #{lead.contactId}
                    </Link>
                  </Group>
                </Alert>
              )}
              <Card withBorder>
                <Group justify="space-between">
                  <Stack gap={2}>
                    <Title order={3}>{fullName}</Title>
                    <Group gap="sm">
                      <StatusBadge status={lead.status} />
                      <AssignAssigneeControl
                        leadId={id}
                        assigneeUserId={lead.assigneeUserId}
                        assigneeLabel={
                          lead.assigneeInfo?.name || lead.assigneeEmail
                        }
                        assigneePicture={lead.assigneeInfo?.picture}
                        readOnly={!canAssign}
                      />
                    </Group>
                  </Stack>
                  {!isConverted && (
                    <Group>
                      <Select
                        placeholder="Change status"
                        data={STATUSES}
                        value={statusDraft}
                        onChange={(v) => setStatusDraft(v as LeadStatus)}
                        w={180}
                      />
                      <Button
                        disabled={!statusDraft}
                        onClick={async () => {
                          await updateStatus.mutateAsync({
                            id,
                            status: statusDraft as LeadStatus,
                          });
                          setStatusDraft(null);
                        }}
                      >
                        Apply
                      </Button>
                    </Group>
                  )}
                </Group>
              </Card>

              <ActivityFeed
                items={buildLeadFeed(data.events, data.comments)}
                onPostComment={(body) =>
                  addComment.mutateAsync({ leadId: id, body })
                }
                posting={addComment.isPending}
              />
            </Stack>
          </Grid.Col>

          <ConvertLeadModal
            lead={lead}
            opened={convertOpen}
            onClose={() => setConvertOpen(false)}
          />

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="md">
              {!isConverted && (
                <ActionsCard
                  lead={lead}
                  canConvert={canConvert}
                  isConvertible={isConvertible}
                  onConvert={() => setConvertOpen(true)}
                />
              )}
              <IdentityCard
                key={`identity-${lead.id}`}
                lead={lead}
                readOnly={isConverted}
              />
              <RequestNotesCard
                key={`narrative-${lead.id}`}
                lead={lead}
                readOnly={isConverted}
              />
              <MarketingCard
                key={`marketing-${lead.id}`}
                lead={lead}
                readOnly={isConverted}
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Container>
    </Paper>
  );
}
